import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "src/store/store";

export type ContactChangeType = "email" | "phone" | null;

export const STAGES = {
  ACKNOWLEDGE: "acknowledge",
  FORM: "form",
  ONFIDO: "onfido",
  COMPLETED: "completed",
  QUIT: "quit",
  CANNOT_PROCESS: "cannot_process",
};

export const BANNER_STAGES = {
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  ACTION_REQUIRED: "action_required",
};

export interface ContactChangeState {
  isContactChangeModalOpen: boolean;
  contactChangeType: ContactChangeType;
  currentStage: string;
  previousStage: string;
  bannerStage: string | null;

  userEmail: string | null;
  userPhone: string | null;
  secondaryAuthValue: string | null;
  changingValueName: "email address" | "phone number" | null;
  secondaryAuthValueName: "email address" | "phone number" | null;
  requestedValue: string | null;
}

const initialState: ContactChangeState = {
  isContactChangeModalOpen: false,
  contactChangeType: null,
  currentStage: STAGES.ACKNOWLEDGE,
  previousStage: STAGES.ACKNOWLEDGE,
  bannerStage: null,

  userEmail: null,
  userPhone: null,
  secondaryAuthValue: null,
  changingValueName: null,
  secondaryAuthValueName: null,
  requestedValue: null,
};

const ContactChangeSlice = createSlice({
  name: "contactChange",
  initialState: initialState,
  reducers: {
    toggleContactChangeModal: (state: ContactChangeState): void => {
      state.isContactChangeModalOpen = !state.isContactChangeModalOpen;
    },
    setContactChangeType: (
      state: ContactChangeState,
      action: PayloadAction<ContactChangeType>,
    ): void => {
      state.contactChangeType = action.payload;
      state.changingValueName =
        state.contactChangeType === "email" ? "email address" : "phone number";
      state.secondaryAuthValueName =
        state.contactChangeType === "email" ? "phone number" : "email address";
      state.secondaryAuthValue =
        state.contactChangeType === "email" ? state.userPhone : state.userEmail;
    },
    setCurrentStage: (state: ContactChangeState, action: PayloadAction<string>): void => {
      state.previousStage = state.currentStage;
      state.currentStage = action.payload;
    },
    setBannerStage: (state: ContactChangeState, action: PayloadAction<string | null>): void => {
      state.bannerStage = action.payload;
    },
    setUserEmail: (state: ContactChangeState, action: PayloadAction<string>): void => {
      state.userEmail = action.payload;
      if (state.contactChangeType === "phone") {
        state.secondaryAuthValue = state.userEmail;
      }
    },
    setUserPhone: (state: ContactChangeState, action: PayloadAction<string>): void => {
      state.userPhone = action.payload;
      if (state.contactChangeType === "email") {
        state.secondaryAuthValue = state.userPhone;
      }
    },
    setRequestedValue: (state: ContactChangeState, action: PayloadAction<string>): void => {
      state.requestedValue = action.payload;
    },
  },
});

export const {
  toggleContactChangeModal,
  setContactChangeType,
  setCurrentStage,
  setBannerStage,
  setUserEmail,
  setUserPhone,
  setRequestedValue,
} = ContactChangeSlice.actions;

export const useContactChangeState = () => useAppSelector((state) => state.contactChange);

export default ContactChangeSlice;
